import { useDecisao } from '../../../common/interface';
import { dia } from '../../../../utils/formato';
import type { CooperadoAba } from '../../types';
import type { EmissaoEmCurso } from './retomada';
import type { EstagioEmissao } from './useEmissao';

interface Props {
  registro: EmissaoEmCurso;
  estagio: EstagioEmissao;
  mensagem: string;
  erro: string | null;
  desfecho: 'concluida' | 'recusada' | null;
  onConcluir: () => void;
  /** Sai da emissão parada e abre o assistente vazio. */
  onOutraViagem: () => void;
  onNavegar?: (aba: CooperadoAba) => void;
}

/**
 * A emissão que parou depois de o CT-e existir — spec 0094.
 *
 * Aparece no lugar do assistente quando o navegador guarda uma emissão parada
 * deste cooperado. Não restaura em silêncio: numa viagem nova, a chave antiga faria
 * o servidor devolver o CT-e anterior, e a ATF sairia descrevendo outra viagem. A
 * escolha é do cooperado, como no app (`cteatf_retomada.dart`).
 */
export function RetomadaDaEmissao({
  registro, estagio, mensagem, erro, desfecho, onConcluir, onOutraViagem, onNavegar,
}: Props) {
  const { janela, confirmar } = useDecisao();
  const processando = estagio !== 'ocioso' && estagio !== 'concluido';
  const documento = `nº ${registro.numCte}${registro.serie ? `, série ${registro.serie}` : ''}`;
  // A conferência do par CT-e ↔ ATF espera 30 minutos antes de agir.
  const antiga = Date.now() - registro.ts > 30 * 60 * 1000;

  async function deixarParada() {
    const sim = await confirmar({
      titulo: 'Deixar esta emissão parada?',
      contexto: `Documento fiscal ${documento}`,
      consequencia: 'Esta viagem fica sem ATF, e sem ela não está autorizada pelo DER. O documento fiscal não serve para outra viagem: se a SEFAZ o autorizou, a conferência automática da cooperativa, que roda de hora em hora, o cancela.',
      rotuloConfirmar: 'Emitir outra viagem',
    });
    if (sim) onOutraViagem();
  }

  if (desfecho === 'concluida') {
    return (
      <section className="co-retomada" aria-labelledby="co-retomada-titulo">
        <div className="co-cartao">
          <h2 id="co-retomada-titulo" className="co-titulo-tela">ATF solicitada</h2>
          <p className="co-apoio">
            O pedido da viagem do documento {documento} foi ao DER. A resposta chega em Minhas ATFs.
          </p>
          <div className="co-acoes">
            {onNavegar && (
              <button type="button" className="co-btn co-btn-primario" onClick={() => onNavegar('acompanhar')}>
                Ver em Minhas ATFs
              </button>
            )}
            <button type="button" className="co-btn co-btn-secundario" onClick={onOutraViagem}>
              Emitir outra viagem
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (desfecho === 'recusada') {
    return (
      <section className="co-retomada" aria-labelledby="co-retomada-titulo">
        <div className="co-cartao">
          <h2 id="co-retomada-titulo" className="co-titulo-tela">A SEFAZ recusou o documento</h2>
          {erro && <p className="co-erro" role="alert">{erro}</p>}
          <p className="co-apoio">Nada foi pedido ao DER. Para viajar, emita de novo com os dados corrigidos.</p>
          <div className="co-acoes">
            <button type="button" className="co-btn co-btn-primario" onClick={onOutraViagem}>
              Emitir outra viagem
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="co-retomada" aria-labelledby="co-retomada-titulo">
      {janela}
      <div className="co-cartao">
        <h2 id="co-retomada-titulo" className="co-titulo-tela">Uma emissão ficou parada</h2>
        <p className="co-apoio">
          {registro.cteAutorizado
            ? 'O documento fiscal já foi autorizado na SEFAZ, e falta pedir a ATF ao DER. Concluir pede a ATF com os dados gravados, sem emitir outro documento.'
            : 'O documento fiscal foi criado, e a autorização da SEFAZ não foi confirmada. Concluir consulta o mesmo documento e pede a ATF, sem emitir outro.'}
        </p>

        {antiga && (
          <p className="co-apoio">
            Ela parou há mais de meia hora. A conferência automática cancela o documento que fica sem ATF, e,
            se já o cancelou, a ATF é recusada e é preciso emitir outra viagem.
          </p>
        )}

        <dl className="co-retomada-dados">
          <dt>Documento</dt>
          <dd>{documento}</dd>
          <dt>Parada desde</dt>
          <dd>{new Date(registro.ts).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}</dd>
          {registro.resumo && (
            <>
              <dt>Viagem</dt>
              <dd>{registro.resumo.origem} → {registro.resumo.destino}</dd>
              <dt>Ida</dt>
              <dd>{dia(registro.resumo.dataIda)}</dd>
            </>
          )}
          {registro.placa && (
            <>
              <dt>Veículo</dt>
              <dd>{registro.placa}</dd>
            </>
          )}
        </dl>

        {processando && <p className="co-progresso" role="status">{mensagem}</p>}
        {erro && !processando && <p className="co-erro" role="alert">{erro}</p>}

        <div className="co-acoes">
          <button type="button" className="co-btn co-btn-primario" onClick={onConcluir} disabled={processando}>
            {processando ? mensagem : erro ? 'Tentar de novo' : 'Concluir a ATF'}
          </button>
          <button type="button" className="co-btn co-btn-secundario" onClick={deixarParada} disabled={processando}>
            Emitir outra viagem
          </button>
        </div>
      </div>
    </section>
  );
}
